import React from 'react';
import { Switch, Text, TextField } from '@chatsdet/ui';
import { ConfigSection } from './ConfigSection';

type AudioAssertionNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

type DeterministicCfg = {
  minDurationMs?: number;
  maxDurationMs?: number;
  minRmsDb?: number;
  silenceFractionMax?: number;
  envelopeCorrelationMin?: number;
};

type NaturalLanguageCfg = {
  prompt?: string;
  model?: 'gpt-audio';
};

const setKey = <K extends keyof DeterministicCfg>(
  prev: DeterministicCfg | undefined,
  key: K,
  value: DeterministicCfg[K] | undefined
): DeterministicCfg => {
  const next: DeterministicCfg = { ...prev };
  if (value === undefined || value === null || (typeof value === 'number' && Number.isNaN(value))) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
};

const numberField = (
  label: string,
  value: number | undefined,
  onChange: (v: number | undefined) => void,
  placeholder = ''
) => (
  <div>
    <Text size="xs" className="text-muted-foreground mb-1 block">
      {label}
    </Text>
    <TextField
      type="number"
      value={typeof value === 'number' ? String(value) : ''}
      onChange={(v) => {
        const trimmed = (v ?? '').trim();
        if (trimmed === '') return onChange(undefined);
        const parsed = Number(trimmed);
        onChange(Number.isFinite(parsed) ? parsed : undefined);
      }}
      placeholder={placeholder}
    />
  </div>
);

export const AudioAssertionNodeConfig: React.FC<AudioAssertionNodeConfigProps> = ({
  localData,
  setLocalData,
}) => {
  const label = (localData.label as string) ?? '';
  // Audio assertions never halt mid-run — fail and fatal both mean "run
  // finishes marked failed" (see handle-audio-assertion-node.ts) — so the UI
  // keeps a binary toggle: warn vs run-failing. A spec-authored 'fatal' value
  // displays as "fails the run" and is preserved unless the user explicitly
  // toggles, which rewrites it to 'fail'.
  const failsRun = localData.severity !== 'warn';
  const deterministic: DeterministicCfg | undefined = localData.deterministic ?? undefined;
  const naturalLanguage: NaturalLanguageCfg | undefined = localData.naturalLanguage ?? undefined;

  const detCount =
    (deterministic?.minDurationMs !== undefined ? 1 : 0) +
    (deterministic?.maxDurationMs !== undefined ? 1 : 0) +
    (deterministic?.minRmsDb !== undefined ? 1 : 0) +
    (deterministic?.silenceFractionMax !== undefined ? 1 : 0) +
    (deterministic?.envelopeCorrelationMin !== undefined ? 1 : 0);
  const nlConfigured = !!naturalLanguage?.prompt && naturalLanguage.prompt.trim().length > 0;

  const updateDeterministic = (updater: (prev: DeterministicCfg) => DeterministicCfg) => {
    setLocalData((prev) => {
      const updated = updater(prev.deterministic ?? {});
      const cleaned = Object.keys(updated).length === 0 ? undefined : updated;
      return { ...prev, deterministic: cleaned };
    });
  };

  const updateNL = (updater: (prev: NaturalLanguageCfg) => NaturalLanguageCfg) => {
    setLocalData((prev) => {
      const updated = updater(prev.naturalLanguage ?? {});
      const cleaned = !updated.prompt || updated.prompt.trim().length === 0 ? undefined : updated;
      return { ...prev, naturalLanguage: cleaned };
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Text size="xs" className="text-muted-foreground mb-1 block">
          Label
        </Text>
        <TextField
          value={label}
          onChange={(v) => setLocalData((prev) => ({ ...prev, label: v ?? '' }))}
          placeholder="Verify audio playback"
        />
      </div>

      <Text size="xs" className="text-muted-foreground block">
        Asserts against the most recent audio capture in the run — set
        <code className="mx-1 px-1 py-0.5 rounded bg-muted">captureAudio</code>
        on the action that triggers playback.
      </Text>

      <div className="flex items-center justify-between gap-3">
        <div>
          <Text size="xs" className="text-muted-foreground block">
            Failure severity
          </Text>
          <Text size="xs" className="text-muted-foreground/70 block">
            On — fails the run when checks don't pass. Off — warns only.
          </Text>
        </div>
        <Switch
          isSelected={failsRun}
          onChange={(v) => setLocalData((prev) => ({ ...prev, severity: v ? 'fail' : 'warn' }))}
        />
      </div>

      <ConfigSection title="Deterministic checks" count={detCount}>
        <div className="flex flex-col gap-3">
          {numberField(
            'Min duration (ms)',
            deterministic?.minDurationMs,
            (v) => updateDeterministic((p) => setKey(p, 'minDurationMs', v)),
            'e.g. 500'
          )}
          {numberField(
            'Max duration (ms)',
            deterministic?.maxDurationMs,
            (v) => updateDeterministic((p) => setKey(p, 'maxDurationMs', v)),
            'e.g. 30000'
          )}
          {numberField(
            'Min RMS (dB)',
            deterministic?.minRmsDb,
            (v) => updateDeterministic((p) => setKey(p, 'minRmsDb', v)),
            'e.g. -40'
          )}
          {numberField(
            'Max silence fraction (0–1)',
            deterministic?.silenceFractionMax,
            (v) => updateDeterministic((p) => setKey(p, 'silenceFractionMax', v)),
            'e.g. 0.5'
          )}
          {numberField(
            'Min envelope correlation (-1..1)',
            deterministic?.envelopeCorrelationMin,
            (v) => updateDeterministic((p) => setKey(p, 'envelopeCorrelationMin', v)),
            'e.g. 0.85'
          )}
        </div>
      </ConfigSection>

      <ConfigSection title="Natural-language check" count={nlConfigured ? 1 : 0}>
        <div className="flex flex-col gap-3">
          <div>
            <Text size="xs" className="text-muted-foreground mb-1 block">
              Predicate
            </Text>
            <TextField
              value={naturalLanguage?.prompt ?? ''}
              onChange={(v) => updateNL((p) => ({ ...p, prompt: v ?? '' }))}
              placeholder="e.g. The voice says 'order confirmed' clearly, in English"
            />
            <Text size="xs" className="text-muted-foreground mt-1 block">
              Evaluated by gpt-audio. Empty disables the NL check.
            </Text>
          </div>
        </div>
      </ConfigSection>
    </div>
  );
};
